import { useEffect, useState } from 'react';
import { buildAndValidate, type BuildResult } from '../../lib/eft/aniCache';
import type { CustomEftDto } from '../../lib/workflows/eftBuilder';

/**
 * What the EFT actually contains, and what the specification thinks of it.
 *
 * Until now the first thing to inspect an EFT was the FBI, days later, and a rejection arrived as a
 * code against a transaction nobody could reopen. This runs the same conformance rules here, while the
 * subject is still in the room and a field can still be corrected.
 *
 * Advisory, not a gate. A specification is a rulebook, not the FBI's own acceptance, and there are real
 * transactions it flags that the FBI takes — so it warns, names what is wrong, and lets the operator
 * decide. It never blocks a submission.
 */

interface Props {
  cacheId: string | null;
  /** Built fresh from the current form; the imagery is stripped before it is sent. */
  dto: CustomEftDto | null;
  /** Impressions still uploading. Validating before they land would report them all as missing. */
  pending: number;
  onResult?: (result: BuildResult | null) => void;
}

export function EbtsValidation({ cacheId, dto, pending, onResult }: Props) {
  const [result, setResult] = useState<BuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!cacheId || !dto || pending > 0) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    buildAndValidate(cacheId, dto)
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        onResult?.(r);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        /*
         * A validation that cannot run must not read as a validation that passed. The result is cleared
         * so the submit button's warning logic sees "unknown" rather than "clean".
         */
        setResult(null);
        onResult?.(null);
        setError(e instanceof Error ? e.message : 'The EFT could not be checked.');
      })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
    // onResult is intentionally excluded: callers pass an inline function, and depending on it would
    // re-run the check on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheId, dto, pending]);

  if (pending > 0) {
    return (
      <div className="ebts-panel">
        <div className="ebts-head"><h4>EFT validation</h4><span className="ebts-muted">{pending} image(s) still uploading…</span></div>
      </div>
    );
  }
  if (busy && !result) {
    return (
      <div className="ebts-panel">
        <div className="ebts-head"><h4>EFT validation</h4><span className="ebts-muted">Checking…</span></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ebts-panel is-unknown">
        <div className="ebts-head"><h4>EFT validation</h4><span className="ebts-muted">Could not be checked</span></div>
        <p className="ebts-note">{error} You can still submit; the file has not been checked against the specification.</p>
      </div>
    );
  }
  if (!result) return null;

  const { valid, issues, recordCount, size, missing, specification, incomplete } = result;
  const kb = (size / 1024).toFixed(0);

  return (
    <div className={`ebts-panel${valid ? ' is-ok' : ' is-bad'}`}>
      <div className="ebts-head">
        <h4>EFT validation</h4>
        <span className={valid ? (incomplete ? 'ebts-muted' : 'ebts-ok') : 'ebts-bad'}>
          {valid
            ? incomplete ? 'No problems found' : 'Conforms'
            : `${issues.length} issue${issues.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="ebts-facts">
        <span><b>{recordCount}</b> records</span>
        <span><b>{kb}</b> KB</span>
        <span title="The rulebook this was checked against">{specification.replace(/_/g, ' ')}</span>
      </div>

      {incomplete && (
        /* Ours, not theirs. Some of the specification's own checks could not run, so a clean result
           here is "nothing found" rather than "fully checked" — saying "Conforms" would be a promise
           the validation did not actually make. */
        <p className="ebts-note">
          Some checks in this specification could not run, so this file has not been fully verified.
          Reported to us automatically — nothing for you to fix.
        </p>
      )}
      {missing.length > 0 && (
        /* Distinct from a violation: the transaction wanted an impression and the cache had none, which
           usually means a capture never finished uploading rather than anything wrong with the file. */
        <p className="ebts-note">
          {missing.length} impression(s) expected by this workflow were not staged
          ({missing.map((m) => `finger ${m.fgp}`).join(', ')}). Re-capture them, or they will be absent from the EFT.
        </p>
      )}

      {!valid && (
        <>
          <button type="button" className="ebts-toggle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide details' : 'Show details'}
          </button>
          {expanded && (
            <ul className="ebts-issues">
              {issues.map((i, n) => (
                <li key={`${i.code}-${i.fieldLabel ?? n}`}>
                  {i.fieldLabel && <code className="ebts-field">{i.fieldLabel}</code>}
                  <span className="ebts-issue-name">{i.name.replace(/_/g, ' ').toLowerCase()}</span>
                  {i.message ? <span className="ebts-issue-msg">{i.message}</span> : <span className="ebts-issue-msg">{i.description}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
